import { PracticeSettingSelect } from "@/components/Selects/PracticeSettingSelect";
import { PracticeSetting } from "@api/shared-runtime/enums";
import {
  AddressSelect,
  AddressOption,
} from "@/components/Selects/AddressSelect";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useOrganization } from "@/hooks/use-organization";
import { useTRPC } from "@/trpc";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";

const addressSchema = z.object({
  address1: z.string(),
  address2: z.string().optional(),
  city: z.string(),
  state: z.string(),
  zipCode: z.string(),
  lat: z.number(),
  lng: z.number(),
});

const facilitySchema = z
  .object({
    id: z.string().optional(),
    name: z.string().min(1),
    isRemote: z.boolean().default(false).nullable(),
    address: addressSchema.nullable(),
    practiceSetting: z.nativeEnum(PracticeSetting),
  })
  .refine(
    (data) => {
      // If not remote, address is required
      if (!data.isRemote) {
        return data.address !== null;
      }
      return true;
    },
    {
      message: "Address is required for non-remote facilities",
      path: ["address"],
    },
  );

type Facility = z.infer<typeof facilitySchema>;

interface FacilityDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (facility: {
    id: string;
    name: string;
    isRemote: boolean | null;
    address1: string | null;
    address2: string | null;
    city: string | null;
    state: string | null;
    zipCode: string | null;
  }) => void;
  facility: {
    id: string;
    name: string;
    isRemote?: boolean;
    practiceSetting: {
      id: string;
      displayText: string;
    } | null;
    address1: string | null;
    address2: string | null;
    city: string | null;
    state: string | null;
    zipCode: string | null;
  } | null;
}
export const FacilityEditDialog = ({
  isOpen,
  onOpenChange,
  onSuccess,
  facility,
}: FacilityDialogProps) => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { selectedOrg, selectedOrgBusinessUnit } = useOrganization();
  const createFacilityMutation = useMutation(
    trpc.facility.create.mutationOptions({
      onSuccess: async (data) => {
        await queryClient.invalidateQueries({
          queryKey: trpc.facility.getAll.queryKey(),
        });
        onOpenChange(false);
        onSuccess(data);
      },
    }),
  );

  const updateFacilityMutation = useMutation(
    trpc.facility.update.mutationOptions({
      onSuccess: async (data) => {
        await queryClient.invalidateQueries({
          queryKey: trpc.facility.getAll.queryKey(),
        });
        onOpenChange(false);
        onSuccess(data);
      },
    }),
  );

  const handleSubmit = (data: Facility) => {
    const addressFields = data.isRemote
      ? {}
      : {
          address1: data.address?.address1,
          address2: data.address?.address2,
          city: data.address?.city,
          state: data.address?.state,
          zipCode: data.address?.zipCode,
        };

    if (facility && facility.id) {
      updateFacilityMutation.mutate({
        id: facility.id,
        name: data.name,
        ...addressFields,
        practiceSettingId: data.practiceSetting,
        isRemote: data.isRemote || false,
        organizationBusinessUnitId: selectedOrgBusinessUnit.id,
      });
    } else {
      createFacilityMutation.mutate({
        name: data.name,
        ...addressFields,
        practiceSettingId: data.practiceSetting,
        organizationId: selectedOrg?.id || "",
        organizationBusinessUnitId: selectedOrgBusinessUnit.id,
        isRemote: data.isRemote || false,
      });
    }
  };

  const buildAddressFromFacility = (
    f: FacilityDialogProps["facility"],
  ): AddressOption | null => {
    if (!f?.address1 || !f?.city || !f?.state || !f?.zipCode) {
      return null;
    }
    return {
      address1: f.address1,
      address2: f.address2 || undefined,
      city: f.city,
      state: f.state,
      zipCode: f.zipCode,
      lat: 0, // We don't have lat/lng from existing data
      lng: 0,
    };
  };

  const form = useForm<Facility>({
    defaultValues: facility
      ? {
          name: facility.name,
          isRemote: facility.isRemote || false,
          address: buildAddressFromFacility(facility),
          practiceSetting:
            (facility.practiceSetting?.id as PracticeSetting) || undefined,
        }
      : {
          name: "",
          isRemote: false,
          address: null,
          practiceSetting: undefined,
        },
    resolver: zodResolver(facilitySchema),
  });

  // Reset form values when facility changes
  useEffect(() => {
    if (isOpen) {
      form.reset(
        facility
          ? {
              name: facility.name,
              isRemote: facility.isRemote || false,
              address: buildAddressFromFacility(facility),
              practiceSetting:
                (facility.practiceSetting?.id as PracticeSetting) || undefined,
            }
          : {
              name: "",
              isRemote: false,
              address: null,
              practiceSetting: undefined,
            },
      );
    }
  }, [facility, isOpen, form.reset]);

  const handleClose = () => {
    form.reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="[&.dialog-content]:max-w-[min(32rem,calc(100vw-2rem))] [&.dialog-content]:max-h-[calc(100dvh-2rem)] w-full overflow-y-auto rounded-2xl border-slate-200 bg-white p-6 sm:rounded-2xl">
        <DialogHeader>
          <DialogTitle>
            {facility ? "Edit Facility" : "Add New Facility"}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={(e) => {
              e.stopPropagation();
              form.handleSubmit(handleSubmit)(e);
            }}
            className="space-y-4 mt-4"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel htmlFor="name">Facility Name</FormLabel>
                  <FormControl>
                    <Input
                      id="name"
                      placeholder="Enter facility name"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="practiceSetting"
              render={({ field }) => (
                <FormItem>
                  <FormLabel htmlFor="practiceSetting">
                    Practice Setting
                  </FormLabel>
                  <FormControl>
                    <PracticeSettingSelect
                      value={field.value ? [field.value] : []}
                      onChange={(values) =>
                        field.onChange(values[0] ?? undefined)
                      }
                      placeholder="Select practice setting"
                      isMulti={false}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isRemote"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between gap-4 rounded-xl border border-slate-400 bg-white p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Remote Facility</FormLabel>
                    <div className="text-sm text-muted-foreground">
                      This facility operates remotely without a physical address
                    </div>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value || false}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {!form.watch("isRemote") && (
              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Address</FormLabel>
                    <FormControl>
                      <AddressSelect
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-2">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  updateFacilityMutation.isPending ||
                  createFacilityMutation.isPending
                }
              >
                {updateFacilityMutation.isPending ||
                createFacilityMutation.isPending
                  ? facility
                    ? "Saving..."
                    : "Creating..."
                  : facility
                    ? "Save Changes"
                    : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
